import React, {useState, useRef} from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../api/api';
import { getAuthErrorMessage } from '../utils/authErrors';
import Loading from './Loading';
import config from '../config';
import { 
    FormSectionStyled, 
    ButtonContainerStyled, 
    FormContainerStyled, 
    InputContainerStyled, 
    InputStyled, 
    LabelStyled, 
    GoogleButtonStyled, 
    GoogleButtonContainerStyled,
    OrContainerStyled,
    HrStyled,
    InputIconStyled,
    ErrorMessageStyled
} from '../styles/Form';
import { NavLinkWrapper } from '../styles/Main';
import { PrimaryButtonStyled } from '../styles/Button';
import iconMap from '../data/iconMap';


const SignIn = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [showV2Captcha, setShowV2Captcha] = useState(false);
    const [v2Token, setV2Token] = useState(null);
    const recaptchaV2Ref = useRef(null);
    const { signIn, signInWithGoogle } = useAuth();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const onSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        
        try {
            let captchaToken = null;
            let captchaVerified = false;
            
            // Try v3 first (invisible)
            if (executeRecaptcha && !showV2Captcha) {
                try {
                    captchaToken = await executeRecaptcha('signin');
                    const captchaResponse = await api.post('/verify-captcha', { token: captchaToken, action: 'signin' });
                    captchaVerified = captchaResponse.data.success;
                } catch (v3Error) {
                    console.warn('reCAPTCHA v3 failed, falling back to v2:', v3Error);
                }
            }
            
            // If v3 failed, show v2 checkbox
            if (!captchaVerified && !showV2Captcha) {
                setLoading(false);
                setShowV2Captcha(true);
                setError('Please complete the security check below.');
                return;
            }
            
            // If v2 is showing, use v2 token
            if (showV2Captcha) {
                if (!v2Token) {
                    setLoading(false);
                    setError('Please complete the reCAPTCHA checkbox.');
                    return;
                }
                // Verify v2 token
                const captchaResponse = await api.post('/verify-captcha', { token: v2Token, action: 'signin', version: 'v2' });
                if (!captchaResponse.data.success) {
                    setLoading(false);
                    setError('reCAPTCHA verification failed. Please try again.');
                    recaptchaV2Ref.current?.reset();
                    setV2Token(null);
                    return;
                }
            }
            
            await signIn(email, password)
            navigate("/");
        } catch (error) {
            setLoading(false)
            setError(getAuthErrorMessage(error))
            console.log(error.code, error.message);
            // Reset v2 captcha on error
            if (showV2Captcha) {
                recaptchaV2Ref.current?.reset();
                setV2Token(null);
            }
        }
    }
    
    const onV2CaptchaChange = (token) => {
        setV2Token(token);
        if (token) {
            setError(''); // Clear error when user completes captcha
        }
    }

    const updateSecureTextEntry = () => {
        setSecureTextEntry(!secureTextEntry);
    }

    const onSubmitWithGoogle = async () => {
        setError('')
        setLoading(true)
        // Fire-and-forget: warm up Cloud Run while we check subscription
        api.get('/health').catch(() => {});
        try {
            await signInWithGoogle()
            // All Google users go to app — free tier auto-provisions on first analysis
            navigate("/");
        } catch (error) {
            setError(getAuthErrorMessage(error))
            console.log(error.code, error.message);
        } finally {
            setLoading(false)
        }
    }
 
    return(
        <main>
            {loading && <Loading message="Signing in..." />}
            <FormSectionStyled>
                <FormContainerStyled>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <h1 style={{ fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Welcome back</h1>
                        <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>Sign in to your PropNinja account</p>
                    </div>
                    <form>                                              
                        <InputContainerStyled>
                            <LabelStyled htmlFor="email-address">
                                Email address
                            </LabelStyled>
                            <InputStyled
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required                                                                                
                                placeholder="Email address"
                                onChange={(e)=>setEmail(e.target.value)}
                            />
                            <InputIconStyled>{iconMap['User_Solid']}</InputIconStyled>
                        </InputContainerStyled>

                        <InputContainerStyled >
                            <LabelStyled htmlFor="password">
                                Password
                            </LabelStyled>
                            <InputStyled
                                id="password"
                                name="password"
                                type={secureTextEntry ? `password`: `text`}
                                autoComplete="current-password"
                                required                                                                                
                                placeholder="Password"
                                onChange={(e)=>setPassword(e.target.value)}
                            />
                            {<InputIconStyled $clickable onClick={updateSecureTextEntry}>{secureTextEntry?iconMap['Eye']:iconMap['Eye_Slash']}</InputIconStyled>}
                        </InputContainerStyled>
                        {error!==''?<ErrorMessageStyled>{error}</ErrorMessageStyled>:null}
                        
                        {showV2Captcha && (
                            <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                                <ReCAPTCHA
                                    ref={recaptchaV2Ref}
                                    sitekey={config.REACT_APP_RECAPTCHA_V2_SITE_KEY}
                                    onChange={onV2CaptchaChange}
                                    onExpired={() => setV2Token(null)}
                                />
                            </div>
                        )}
                        
                        <ButtonContainerStyled>
                            <PrimaryButtonStyled  
                                $grouped                                
                                onClick={onSubmit}                                        
                            >      
                                Sign in                                                                  
                            </PrimaryButtonStyled>

                        </ButtonContainerStyled>
                        <OrContainerStyled><HrStyled/><p>OR</p><HrStyled/></OrContainerStyled>
                        <GoogleButtonContainerStyled>
                            <GoogleButtonStyled onClick={onSubmitWithGoogle}>Continue with Google</GoogleButtonStyled>
                        </GoogleButtonContainerStyled>
                                                
                    </form>
                    
                    <p>
                        No account yet? {' '}
                        <NavLinkWrapper to="/signup">
                            Sign up
                        </NavLinkWrapper>
                    </p>
                    <p>
                        Need password reset? {' '}
                        <NavLinkWrapper to="/reset">
                            Reset Password
                        </NavLinkWrapper>
                    </p>                        
                </FormContainerStyled>
            </FormSectionStyled>
        </main>
    )
}
 
export default SignIn